import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { games } from "@/mocks/games";
import { filterGames, getCategoryByKey, paginate, sortGames, type SortKey } from "@/utils/gameUtils";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import GameCard from "@/components/feature/GameCard";
import FilterSidebar from "./components/FilterSidebar";

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "hot", label: "按热度", icon: "ri-fire-line" },
  { key: "new", label: "按发布时间", icon: "ri-time-line" },
  { key: "rating", label: "按评分", icon: "ri-star-line" },
];

const PAGE_SIZE = 8;

export default function Category() {
  const { type = "" } = useParams();
  const category = getCategoryByKey(type);

  const [theme, setTheme] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("hot");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setTheme("");
    setTags([]);
    setSort("hot");
    setPage(1);
    setFilterOpen(false);
  }, [type]);

  useSeo({
    title: category ? `【${category.name}】网页游戏在线试玩 - 网页游戏直接玩` : "游戏分类 - 网页游戏直接玩",
    description: category
      ? `${category.name}分类下汇集多款热门网页游戏，${category.desc}免下载安装，打开网页即可在线试玩。`
      : "浏览网页游戏直接玩的全部游戏分类。",
    keywords: category
      ? `${category.name},${category.name}网页游戏,在线试玩,网页游戏直接玩`
      : "网页游戏分类,在线试玩,网页游戏直接玩",
  });

  const filtered = useMemo(
    () => sortGames(filterGames(games, { category: type, theme, tags }), sort),
    [type, theme, tags, sort],
  );

  const paged = useMemo(() => paginate(filtered, page, PAGE_SIZE), [filtered, page]);

  const resetFilters = () => {
    setTheme("");
    setTags([]);
    setPage(1);
  };

  if (!category) {
    return (
      <div className="mx-auto max-w-screen2xl px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="分类不存在"
          description="你访问的游戏分类不存在或已调整，返回首页看看其他分类吧。"
          action={
            <Link
              to="/"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-screen2xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "游戏分类" }, { label: category.name }]} />

      <div className="mt-5 flex items-start gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
          <i className={`${category.icon} text-2xl`}></i>
        </span>
        <div>
          <h1 className="text-2xl font-black text-foreground-900 md:text-3xl">{category.name}</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{category.desc}</p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
            <FilterSidebar
              categoryKey={type}
              theme={theme}
              tags={tags}
              onThemeChange={(value) => {
                setTheme(value);
                setPage(1);
              }}
              onTagsChange={(value) => {
                setTags(value);
                setPage(1);
              }}
              onReset={resetFilters}
            />
          </div>
        </aside>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-medium text-foreground-700 transition hover:border-primary-300 hover:text-primary-600 lg:hidden"
              >
                <i className="ri-equalizer-line"></i>
                筛选
                {tags.length + (theme ? 1 : 0) > 0 && (
                  <span className="ml-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-[11px] font-bold text-background-50">
                    {tags.length + (theme ? 1 : 0)}
                  </span>
                )}
              </button>
              <span className="text-sm text-foreground-500">
                共 <strong className="text-foreground-900">{filtered.length}</strong> 款游戏
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1 rounded-md bg-background-100 p-1">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setSort(option.key);
                    setPage(1);
                  }}
                  className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition ${
                    sort === option.key
                      ? "border border-background-200 bg-background-50 text-primary-600"
                      : "border border-transparent text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  <i className={option.icon}></i>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            {paged.items.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                {paged.items.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="ri-search-eye-line"
                title="暂无符合条件的游戏"
                description="试着放宽筛选条件，或重置筛选查看全部游戏。"
                action={
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
                  >
                    <i className="ri-refresh-line"></i>
                    重置筛选
                  </button>
                }
              />
            )}
          </div>

          <Pagination page={paged.page} totalPages={paged.totalPages} onChange={setPage} />
        </div>
      </div>

      {filterOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden" onClick={() => setFilterOpen(false)}>
          <div className="absolute inset-0 bg-foreground-950/60 animate-fade-in" />
          <div
            className="relative ml-auto h-full w-[85%] max-w-sm overflow-y-auto bg-background-50 p-5 animate-fade-in"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-bold text-foreground-900">筛选游戏</h2>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                aria-label="关闭筛选"
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 transition hover:bg-background-100"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterSidebar
              categoryKey={type}
              theme={theme}
              tags={tags}
              onThemeChange={(value) => {
                setTheme(value);
                setPage(1);
              }}
              onTagsChange={(value) => {
                setTags(value);
                setPage(1);
              }}
              onReset={resetFilters}
            />
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-6 w-full rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600"
            >
              查看 {filtered.length} 款游戏
            </button>
          </div>
        </div>
      )}
    </div>
  );
}